import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { hotmartSalesLink } from '@/lib/links';
import {
  ArrowRight,
  Sparkles,
  Waves,
  Anchor,
  BookOpen,
  Brain,
  Gem,
  Star,
} from 'lucide-react';
import heroPortrait from '@/assets/hero-portrait.jpg';
import glamBrain from '@/assets/glam-brain.jpg';
import glamBubbles from '@/assets/glam-bubbles.jpg';
import glamHands from '@/assets/glam-hands.jpg';
import glamJournal from '@/assets/glam-journal.jpg';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleStart = () => navigate('/auth');

  const pillars = [
    {
      id: '01',
      kicker: 'Regulação Emocional',
      title: 'NeuroGlitter',
      body: 'Para os dias em que a mente fica pesada e sem cor: exercícios curtos que devolvem ânimo, leveza e motivação.',
      tag: 'BLUEPRINT',
      Icon: Sparkles,
      image: glamBubbles,
    },
    {
      id: '02',
      kicker: 'Micro-hábitos & Foco',
      title: 'Task Palace',
      body: 'Saia da paralisação: quebre qualquer tarefa em passos pequenos, decida o próximo e veja o que já fez.',
      tag: 'SYSTEM',
      elevated: true,
      Icon: Gem,
      image: glamJournal,
    },
    {
      id: '03',
      kicker: 'Teoria Polivagal & SOS',
      title: 'Pés no Chão',
      body: 'Quando a ansiedade dispara: respiração guiada, aterramento e um botão SOS que acalma o corpo em poucos minutos.',
      tag: 'ANCHOR',
      Icon: Anchor,
      image: glamHands,
    },
    {
      id: '04',
      kicker: 'TCC & Journaling',
      title: 'Bullet Journal Vivo',
      body: 'Anote o que sente, entenda seus gatilhos e acompanhe seus padrões ao longo das semanas.',
      tag: 'MEMORY',
      Icon: BookOpen,
      image: glamBrain,
    },
  ];

  const themes = [
    'Neurociência aplicada',
    'Terapia Cognitivo-Comportamental',
    'Teoria Polivagal',
    'Regulação do sistema nervoso',
    'Neurodiversidade',
    'Autoconhecimento clínico',
  ];

  const benefits = [
    {
      n: '01',
      t: 'Clareza mental',
      d: 'Pare de ruminar: dê nome ao que sente em segundos e entenda o que está acontecendo dentro de você.',
      Icon: Brain,
    },
    {
      n: '02',
      t: 'Regulação real',
      d: 'Alivie a tensão quando o corpo está sobrecarregado, com práticas guiadas para os momentos difíceis.',
      Icon: Waves,
    },
    {
      n: '03',
      t: 'Evolução visível',
      d: 'Veja sua evolução semana a semana e perceba o que realmente funciona para você.',
      Icon: Star,
    },
  ];

  return (
    <main className="min-h-screen w-full relative overflow-hidden bg-background text-foreground">
      <Helmet>
        <title>NeuroEixo — Alívio da ansiedade e regulação emocional no dia a dia</title>
        <meta name="description" content="App criado por psicóloga para aliviar ansiedade, parar a ruminação e sair da paralisação. Práticas guiadas de respiração, SOS e registro de emoções. Assine pela Hotmart." />
        <link rel="canonical" href="https://neuroeixovip.lovable.app/" />
        <meta property="og:title" content="NeuroEixo — Alívio da ansiedade e regulação emocional no dia a dia" />
        <meta property="og:description" content="Treine, no dia a dia, a capacidade de se acalmar, ter clareza e decidir. App criado pela psicóloga Marília Teixeira." />
        <meta property="og:url" content="https://neuroeixovip.lovable.app/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "NeuroEixo",
          "url": "https://neuroeixovip.lovable.app/",
          "logo": "https://neuroeixovip.lovable.app/favicon.ico",
          "description": "Plataforma de regulação emocional guiada por neuropsicologia, TCC e Teoria Polivagal.",
          "founder": { "@type": "Person", "name": "Marília Angela Teixeira" },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "NeuroEixo",
          "url": "https://neuroeixovip.lovable.app/",
          "inLanguage": "pt-BR",
        })}</script>
      </Helmet>

      {/* ============ TOP BAR ============ */}
      <header className="relative z-20 flex items-center justify-between px-6 md:px-12 lg:px-20 pt-8 pb-2">
        <div className="flex items-center gap-3">
          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-foreground via-muted-foreground to-foreground/40 shadow-inner" />
          <span className="font-display text-xl tracking-tight text-foreground">NeuroEixo</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          <span>Platinum · 2026</span>
          <span>pt-BR</span>
        </div>
        <button
          onClick={handleStart}
          aria-label="Entrar na sua conta NeuroEixo"
          className="text-[10px] uppercase tracking-[0.3em] text-foreground border border-foreground/30 hover:border-foreground px-4 py-2 transition-colors"
        >
          Entrar
        </button>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative w-full px-6 md:px-12 lg:px-20 pt-10 md:pt-16 pb-24 md:pb-32">
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — headline + CTA */}
          <div className="lg:col-span-7 flex flex-col space-y-10 animate-fade-in relative z-10">
            <div>
              <span className="text-[10px] uppercase tracking-[0.35em] font-medium text-muted-foreground mb-8 flex items-center gap-3">
                <span className="h-px w-8 bg-foreground/40" />
                Edição Platinum · 2026
              </span>
              <h1
                aria-label="Você em Alta Definição — NeuroEixo"
                className="font-display text-[3.6rem] sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.88] text-foreground tracking-[-0.02em]"
              >
                Você em
                <br />
                <span className="italic text-muted-foreground">alta</span>
                <br />
                definição.
              </h1>
            </div>

            <div className="max-w-lg space-y-8">
              <p className="font-body text-lg md:text-xl font-light leading-relaxed text-foreground/80">
                Quando a ansiedade dispara, a mente não para de ruminar ou você trava diante de uma decisão, o NeuroEixo te guia, passo a passo, para <em className="italic text-foreground">se acalmar, ter clareza e voltar a agir</em>. Criado por psicóloga, para usar no celular, a qualquer hora.
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-2">
                <Button
                  onClick={handleStart}
                  size="lg"
                  className="group h-14 px-10 bg-foreground hover:bg-foreground/90 text-background font-body font-medium text-sm tracking-[0.25em] uppercase rounded-none shadow-xl transition-all duration-500"
                >
                  Entrar no Eixo
                  <ArrowRight className="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 rounded-none border-foreground/30 hover:border-foreground hover:bg-transparent text-foreground font-body font-medium text-sm tracking-[0.25em] uppercase"
                >
                  <a href={hotmartSalesLink('landing_hero')} target="_blank" rel="noopener noreferrer">
                    Assinar agora
                  </a>
                </Button>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Acesso seguro</span>
                  <span className="text-xs font-body font-semibold text-foreground tracking-wide">via Hotmart</span>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4 border-t border-border">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-muted via-secondary to-muted-foreground/30 shadow-inner" />
                <div className="flex flex-col text-sm">
                  <span className="font-body font-medium text-foreground">Marília Angela Teixeira</span>
                  <span className="font-body text-muted-foreground text-xs tracking-wide">Psicóloga TCC · Neuropsicóloga</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — glam portrait */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-secondary/40 shadow-2xl">
              <img
                src={heroPortrait}
                alt="Retrato editorial platinado representando clareza mental e regulação emocional em alta definição"
                width={1280}
                height={1600}
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
              {/* Editorial overlay marks */}
              <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none">
                <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-foreground/70 bg-background/60 backdrop-blur px-2 py-1">
                  NeuroEixo · Vol. I
                </span>
                <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-foreground/70 bg-background/60 backdrop-blur px-2 py-1">
                  HD · 2026
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end pointer-events-none">
                <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-foreground/70 bg-background/60 backdrop-blur px-2 py-1 leading-tight">
                  cap. 01<br />o eixo
                </div>
                <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-foreground/70 bg-background/60 backdrop-blur px-2 py-1 text-right leading-tight">
                  clareza<br />em foco
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
              <span>N—001 / Retrato editorial</span>
              <span>© VIP 2026</span>
            </div>
          </div>
        </div>

        {/* Marquee-ish strip */}
        <div className="relative max-w-7xl mx-auto mt-20 border-y border-border py-4 overflow-hidden">
          <div className="flex items-center gap-10 text-[11px] uppercase tracking-[0.4em] text-muted-foreground whitespace-nowrap">
            <span>Neuropsicologia</span>
            <span className="text-foreground/30">✦</span>
            <span>TCC clínica</span>
            <span className="text-foreground/30">✦</span>
            <span>Teoria Polivagal</span>
            <span className="text-foreground/30">✦</span>
            <span>Regulação em HD</span>
            <span className="text-foreground/30">✦</span>
            <span>Blueprint psicológico</span>
            <span className="text-foreground/30">✦</span>
            <span>Editorial · Platinum</span>
          </div>
        </div>
      </section>

      {/* ============ PILLARS — editorial spread ============ */}
      <section className="relative w-full px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-secondary/30 border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <span className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">O que você encontra</span>
              <h2 className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] text-foreground">
                Ajuda prática<br /><span className="italic text-muted-foreground">para cada momento.</span>
              </h2>
            </div>
            <p className="max-w-sm font-body text-sm text-muted-foreground leading-relaxed">
              Força de vontade não basta. Calma, clareza e decisão são capacidades que se treinam — e aqui você treina todos os dias.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {pillars.map((p, i) => {
              const Icon = p.Icon;
              return (
                <article
                  key={p.id}
                  className={`group relative border border-border bg-card overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-700 animate-fade-in ${
                    p.elevated ? 'md:-translate-y-4' : ''
                  }`}
                  style={{ animationDelay: `${0.1 + i * 0.08}s` }}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
                    <img
                      src={p.image}
                      alt={`${p.title}: ${p.kicker} — ${p.body}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                      <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-foreground/70 bg-background/70 backdrop-blur px-2 py-1">
                        {p.id} · {p.tag}
                      </span>
                      <div className="h-9 w-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center border border-foreground/10">
                        <Icon className="h-4 w-4 text-foreground" strokeWidth={1.25} />
                      </div>
                    </div>
                  </div>
                  <div className="p-7 md:p-8">
                    <p className="text-[10px] text-muted-foreground font-body font-medium uppercase tracking-[0.25em]">
                      {p.kicker}
                    </p>
                    <h3 className="mt-2 font-display text-3xl md:text-4xl text-foreground leading-none">
                      {p.title}
                    </h3>
                    <p className="mt-4 text-sm md:text-base font-body text-muted-foreground leading-relaxed">
                      {p.body}
                    </p>
                    <div className="mt-6 h-px w-full bg-border group-hover:bg-foreground/40 transition-colors duration-500" />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ MANIFESTO ============ */}
      <section className="relative bg-background">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <span className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">Manifesto</span>
          <blockquote className="font-display mt-10 text-4xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground text-balance">
            "Você não precisa lutar contra sua mente. <span className="italic text-muted-foreground">Você precisa vê-la — em alta definição.</span>"
          </blockquote>
          <div className="mt-12 flex items-center justify-center gap-4">
            <div className="h-px w-16 bg-border" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Marília Angela Teixeira</span>
            <div className="h-px w-16 bg-border" />
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className="relative w-full px-6 md:px-12 lg:px-20 py-24 md:py-32 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <span className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">Benefícios</span>
              <h2 className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92] text-foreground">
                Do caos<br />ao <span className="italic text-muted-foreground">eixo.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-6">
              <p className="font-body text-lg md:text-xl text-muted-foreground leading-relaxed">
                Sem promessas mágicas e sem “consertar quem você é”. O NeuroEixo te dá estrutura, prática e acompanhamento para lidar melhor com tensão, sobrecarga mental, ruminação e paralisação.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            {benefits.map((b) => {
              const Icon = b.Icon;
              return (
                <div key={b.n} className="bg-card p-8 md:p-10 group hover:bg-secondary/50 transition-colors duration-500">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground tracking-[0.2em]">{b.n}</span>
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-secondary via-muted to-secondary flex items-center justify-center border border-foreground/10 shadow-inner">
                      <Icon className="h-4 w-4 text-foreground" strokeWidth={1.25} />
                    </div>
                  </div>
                  <h3 className="mt-6 font-display text-3xl md:text-4xl text-foreground leading-none">{b.t}</h3>
                  <p className="mt-4 font-body text-sm md:text-base text-muted-foreground leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ THEMES INDEX ============ */}
      <section className="relative border-t border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <span className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">Base científica</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl leading-[0.95] text-foreground">
              Feito com<br /><span className="italic text-muted-foreground">base clínica.</span>
            </h2>
            <p className="mt-6 font-mono text-[10px] text-muted-foreground/80 tracking-[0.3em]">TCC · NEUROPSICOLOGIA · TEORIA POLIVAGAL</p>
          </div>
          <ul className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {themes.map((t, i) => (
              <li
                key={t}
                className="flex items-baseline justify-between border-b border-border py-6 group hover:border-foreground/40 transition-colors"
              >
                <span className="font-display text-2xl md:text-3xl text-foreground group-hover:italic transition-all">{t}</span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative border-t border-border bg-background overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-28 md:py-40 text-center relative">
          <img
            src={glamBrain}
            alt=""
            aria-hidden
            loading="lazy"
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] max-w-[80%] opacity-25 mix-blend-luminosity select-none"
          />
          <div className="relative">
            <span className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">Assinatura individual · Acesso imediato</span>
            <h2 className="mt-8 font-display text-6xl md:text-7xl lg:text-8xl leading-[0.92] text-foreground">
              Entre no eixo.<br /><span className="italic text-muted-foreground">em alta definição.</span>
            </h2>
            <p className="mt-10 font-body text-lg md:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed">
              Assine e receba acesso ao app NeuroEixo, à comunidade e a orientações para usar tudo na prática. Você não precisa enfrentar isso sozinha.
            </p>
            <div className="mt-12 flex flex-col items-center gap-6">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={handleStart}
                  size="lg"
                  className="group h-16 px-14 bg-foreground hover:bg-foreground/90 text-background font-body font-medium text-sm tracking-[0.3em] uppercase rounded-none shadow-2xl transition-all duration-500"
                >
                  Entrar no Eixo
                  <ArrowRight className="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-16 px-12 rounded-none border-foreground/30 hover:border-foreground hover:bg-transparent text-foreground font-body font-medium text-sm tracking-[0.3em] uppercase"
                >
                  <a href={hotmartSalesLink('landing_final')} target="_blank" rel="noopener noreferrer">
                    Assinar agora
                  </a>
                </Button>
              </div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                Acesso via Hotmart · Assinatura segura
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-6 text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
            <span>TCC</span>
            <span>Neuropsicologia</span>
            <span>Teoria Polivagal</span>
            <Link to="/links" className="underline underline-offset-4 hover:text-foreground transition-colors">
              Todos os links
            </Link>
            <Link to="/qr" className="underline underline-offset-4 hover:text-foreground transition-colors">
              QR codes
            </Link>
          </div>
          <p className="text-[10px] text-muted-foreground font-body tracking-[0.2em] uppercase">
            © 2026 VIP — Vida, Inovação e Psicologia
          </p>
        </div>
      </footer>
    </main>
  );
};

export default LandingPage;
